import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { authService } from "../services/authService";
import toast from "react-hot-toast";
import { LogIn, Mail, Lock, Loader2, ShieldCheck, RotateCcw, ArrowLeft } from "lucide-react";

// How long (seconds) before the OTP expires — keep in sync with
// OTP_LOGIN_CODE_EXPIRES_SECONDS in your .env (default 120).
const OTP_EXPIRY_SECONDS = 120;
// How many OTP digits — keep in sync with OTP_LOGIN_CODE_LENGTH (default 4).
const OTP_LENGTH = 4;

function OtpModal({ email, onVerified, onBack }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(OTP_EXPIRY_SECONDS);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  // Start countdown on mount / after resend
  const startCountdown = useCallback(() => {
    clearInterval(timerRef.current);
    setCountdown(OTP_EXPIRY_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCountdown();
    // Auto-focus first box
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
    return () => clearInterval(timerRef.current);
  }, [startCountdown]);

  const handleChange = (idx, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    const next = [...digits];
    next[idx] = value.slice(-1); // one digit per box
    setDigits(next);
    if (value && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = "";
        setDigits(next);
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1)
      inputRefs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    const otp = digits.join("");
    if (otp.length < OTP_LENGTH) {
      toast.error(`Please enter the ${OTP_LENGTH}-digit OTP`);
      return;
    }
    setLoading(true);
    try {
      const data = await authService.verifyOtp(email, otp);
      if (data.token && data.user) {
        onVerified(data.token, data.user);
      } else {
        toast.error("Invalid response from server");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.errors?.otp?.[0] ||
        error.response?.data?.message ||
        "Invalid OTP. Please try again."
      );
      // Clear digits on wrong OTP
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await authService.resendOtp(email);
      toast.success("A new OTP has been sent to your email.");
      setDigits(Array(OTP_LENGTH).fill(""));
      startCountdown();
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div
        className="w-full max-w-md bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 space-y-6 animate-slide-up"
        style={{ animation: "slideUp 0.3s ease-out" }}
      >
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Verify Your Identity
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Enter the {OTP_LENGTH}-digit OTP sent to
          </p>
          <p className="text-sm font-semibold text-orange-500 break-all">{email}</p>
        </div>

        {/* OTP input boxes */}
        <form onSubmit={handleVerify}>
          <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
            {digits.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                disabled={loading}
                className={`w-14 h-14 text-center text-2xl font-bold rounded-2xl border-2 transition-all duration-200 outline-none
                  bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                  ${digit
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                    : "border-gray-200 dark:border-gray-600"
                  }
                  focus:border-orange-500 focus:bg-orange-50 dark:focus:bg-orange-900/20
                  disabled:opacity-50`}
                aria-label={`OTP digit ${idx + 1}`}
              />
            ))}
          </div>

          {/* Countdown */}
          <div className="text-center mb-4">
            {countdown > 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                OTP expires in{" "}
                <span className={`font-semibold ${countdown <= 30 ? "text-red-500" : "text-orange-500"}`}>
                  {fmt(countdown)}
                </span>
              </p>
            ) : (
              <p className="text-sm text-red-500 font-medium">OTP has expired</p>
            )}
          </div>

          {/* Verify Button */}
          <button
            type="submit"
            disabled={loading || digits.join("").length < OTP_LENGTH}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-amber-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5" />
                <span>Verify OTP</span>
              </>
            )}
          </button>
        </form>

        {/* Resend + Back */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>

          <button
            type="button"
            onClick={handleResend}
            disabled={countdown > 0 || resendLoading || loading}
            className="flex items-center gap-1.5 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {resendLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Resend OTP
          </button>
        </div>
      </div>
    </div>
  );
}

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ||
  "0x4AAAAAAFbrokVKCBgEMH9I";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpEmail, setOtpEmail] = useState(null); // non-null = show OTP modal
  const [turnstileToken, setTurnstileToken] = useState("");
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  // Refs for the widget container DOM node and the widgetId Turnstile returns.
  // widgetId is needed for reset() (single-use token lifecycle).
  const turnstileContainerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Render the Turnstile widget explicitly once the API is available.
  const renderWidget = useCallback(() => {
    if (!turnstileContainerRef.current) return;
    if (!window.turnstile) return;
    // Avoid double-rendering
    if (widgetIdRef.current !== null) return;

    widgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      action: "login",
      callback(token) {
        setTurnstileToken(token);
      },
      "error-callback"() {
        setTurnstileToken("");
      },
      "expired-callback"() {
        setTurnstileToken("");
      },
    });
  }, []);

  useEffect(() => {
    // If the script already executed before React mounted, render immediately.
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Otherwise, wait for the script to signal readiness.
    window.onloadTurnstileCallback = renderWidget;

    // Fallback: poll briefly in case the event-based callback fires before
    // this component mounts or the global wasn't wired in time.
    const poll = setInterval(() => {
      if (window.turnstile) {
        clearInterval(poll);
        renderWidget();
      }
    }, 200);

    return () => {
      clearInterval(poll);
      // Clean up the widget on unmount so it doesn't leak across navigations.
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget]);

  // Reset the widget (issues a fresh challenge + clears the consumed token).
  const resetWidget = useCallback(() => {
    setTurnstileToken("");
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    if (!turnstileToken) {
      toast.error("Please complete the security check.");
      return;
    }

    setLoading(true);

    try {
      const data = await authService.login(email, password, turnstileToken);

      if (data.otp_required) {
        // Credentials OK — show OTP modal
        toast.success("OTP sent to your email address!");
        setOtpEmail(data.email);
        // Token is consumed; reset so any retry from the OTP-back flow gets a fresh one.
        resetWidget();
      } else if (data.token && data.user) { // OTP disabled on backend (ENABLE_OTP_LOGIN=false) — token returned immediately
        handleOtpVerified(data.token, data.user);
      } else {
        // Shouldn't happen with the new backend, but handle gracefully
        toast.error("Unexpected response from server");
        resetWidget();
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(
        error.response?.data?.errors?.email?.[0] ||
        error.response?.data?.message ||
        "Login failed. Please try again."
      );
      // Token is single-use — reset so the user can retry.
      resetWidget();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerified = (token, user) => {
    setAuth(token, user);
    toast.success("Login successful!");
    navigate("/select-product");
  };

  const handleOtpBack = () => {
    setOtpEmail(null);
    setPassword(""); // clear password for security
    // Issue a fresh challenge when the user returns to the login form.
    resetWidget();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-stone-900 transition-colors duration-300 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
            <img
              src="/pwa1-192x192.png"
              alt="Galaxy Logo"
              className="w-full h-full object-contain drop-shadow-2xl"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Galaxy
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Sign in to continue
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 space-y-6 transition-colors duration-300">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:border-orange-500 focus:bg-white focus:outline-none transition-all duration-200 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:bg-gray-700 dark:placeholder-gray-400"
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:border-orange-500 focus:bg-white focus:outline-none transition-all duration-200 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:bg-gray-700 dark:placeholder-gray-400"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Turnstile Widget */}
            <div
              ref={turnstileContainerRef}
              id="cf-turnstile-login"
              className="flex justify-center"
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !turnstileToken}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-amber-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Sending OTP...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-500 mt-6">
          Secure login powered by Laravel Sanctum
        </p>
      </div>

      {/* OTP Modal — rendered on top when otpEmail is set */}
      {otpEmail && (
        <OtpModal
          email={otpEmail}
          onVerified={handleOtpVerified}
          onBack={handleOtpBack}
        />
      )}
    </div>
  );
}

export default Login;
